// Whisky lookup service for barcode scanning
// This service attempts to fetch whisky information from various sources

class WhiskyLookupService {
  constructor() {
    // Fallback database of common whisky barcodes
    this.fallbackDatabase = {
      // Example entries - in a real app, this would be much larger
      '5000299225011': {
        name: 'Glenfiddich 12 Year Old',
        distillery: 'Glenfiddich',
        region: 'Speyside',
        age: '12 years',
        abv: '40',
        type: 'Single Malt'
      },
      '5000299225028': {
        name: 'Glenfiddich 15 Year Old Solera Reserve',
        distillery: 'Glenfiddich',
        region: 'Speyside',
        age: '15 years',
        abv: '40',
        type: 'Single Malt'
      },
      '5000299225035': {
        name: 'Glenfiddich 18 Year Old Ancient Reserve',
        distillery: 'Glenfiddich',
        region: 'Speyside',
        age: '18 years',
        abv: '40',
        type: 'Single Malt'
      },
      '5010314301309': {
        name: 'Macallan 12 Year Old Sherry Oak',
        distillery: 'The Macallan',
        region: 'Speyside',
        age: '12 years',
        abv: '40',
        type: 'Single Malt'
      },
      '5010314301316': {
        name: 'Macallan 18 Year Old Sherry Oak',
        distillery: 'The Macallan',
        region: 'Speyside',
        age: '18 years',
        abv: '43',
        type: 'Single Malt'
      },
      '5000267014104': {
        name: 'Lagavulin 16 Year Old',
        distillery: 'Lagavulin',
        region: 'Islay',
        age: '16 years',
        abv: '43',
        type: 'Single Malt'
      },
      '5000267014111': {
        name: 'Lagavulin Distillers Edition',
        distillery: 'Lagavulin',
        region: 'Islay',
        age: 'NAS',
        abv: '43',
        type: 'Single Malt'
      },
      '5000267130107': {
        name: 'Ardbeg 10 Year Old',
        distillery: 'Ardbeg',
        region: 'Islay',
        age: '10 years',
        abv: '46',
        type: 'Single Malt'
      },
      '5000267130114': {
        name: 'Ardbeg Uigeadail',
        distillery: 'Ardbeg',
        region: 'Islay',
        age: 'NAS',
        abv: '54.2',
        type: 'Single Malt'
      },
      '5000267130121': {
        name: 'Ardbeg Corryvreckan',
        distillery: 'Ardbeg',
        region: 'Islay',
        age: 'NAS',
        abv: '57.1',
        type: 'Single Malt'
      }
    };
  }

  async lookupWhisky(barcode) {
    console.log('Looking up whisky for barcode:', barcode);
    
    try {
      // Query multiple sources simultaneously for better data
      const lookupPromises = [
        this.lookupOpenFoodFacts(barcode).catch(err => {
          console.warn('Open Food Facts lookup failed:', err);
          return null;
        }),
        this.lookupUPCDatabase(barcode).catch(err => {
          console.warn('UPC Database lookup failed:', err);
          return null;
        }),
        this.lookupWhiskyBase(barcode).catch(err => {
          console.warn('WhiskyBase lookup failed:', err);
          return null;
        })
      ];

      // Wait for all API calls to complete
      const [openFoodFactsData, upcDatabaseData, whiskyBaseData] = await Promise.all(lookupPromises);
      
      console.log('API Results:');
      console.log('- Open Food Facts:', openFoodFactsData);
      console.log('- UPC Database:', upcDatabaseData);
      console.log('- WhiskyBase:', whiskyBaseData);

      // Collect all available data sources
      const availableSources = [
        openFoodFactsData,
        upcDatabaseData,
        whiskyBaseData
      ].filter(data => data !== null);

      // If we have multiple sources, combine them intelligently
      if (availableSources.length > 1) {
        const combinedData = this.combineMultipleWhiskyData(availableSources);
        console.log(`Combined whisky data from ${availableSources.length} sources:`, combinedData);
        return combinedData;
      }

      // If we only have one source, use it
      if (availableSources.length === 1) {
        console.log('Using single source data:', availableSources[0]);
        return availableSources[0];
      }

      // Fallback to our local database
      const fallbackData = this.lookupFallbackDatabase(barcode);
      if (fallbackData) {
        console.log('Found whisky data from fallback database:', fallbackData);
        return fallbackData;
      }

      // Last resort: try to extract basic info from generic product data
      const genericData = await this.lookupGenericProduct(barcode);
      if (genericData) {
        console.log('Found generic product data:', genericData);
        return genericData;
      }

      return null;
    } catch (error) {
      console.error('Error looking up whisky:', error);
      return null;
    }
  }

  async lookupOpenFoodFacts(barcode) {
    try {
      const response = await fetch(`https://world.openfoodfacts.org/api/v0/product/${barcode}.json`);
      const data = await response.json();

      if (data.status === 1 && data.product) {
        const product = data.product;
        
        // Check if this is likely a whisky/spirits product
        const categories = product.categories_tags || [];
        const isSpirits = categories.some(cat => 
          cat.includes('spirits') || 
          cat.includes('whisky') || 
          cat.includes('whiskey') ||
          cat.includes('scotch') ||
          cat.includes('bourbon')
        );

        if (isSpirits || this.isLikelyWhisky(product.product_name || '')) {
          return this.parseWhiskyFromProduct(product);
        }
      }
      return null;
    } catch (error) {
      console.error('Open Food Facts lookup failed:', error);
      return null;
    }
  }

  async lookupUPCDatabase(barcode) {
    try {
      console.log(`Looking up UPC: ${barcode} in UPC Database`);
      
      // Try multiple CORS proxy services for reliability
      const proxies = [
        'https://api.allorigins.win/raw?url=',
        'https://thingproxy.freeboard.io/fetch/'
      ];
      
      for (const proxyUrl of proxies) {
        try {
          const targetUrl = `https://api.upcitemdb.com/prod/trial/lookup?upc=${barcode}`;
          const fullUrl = proxyUrl + encodeURIComponent(targetUrl);
          
          console.log(`Trying proxy: ${proxyUrl}`);
          
          const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (!response.ok) {
            console.log(`Proxy ${proxyUrl} returned status: ${response.status}`);
            continue; // Try next proxy
          }

          const responseText = await response.text();
          let data;
          
          try {
            // Parse the JSON response directly (raw proxy should give us direct JSON)
            data = JSON.parse(responseText);
          } catch (e) {
            console.log(`Failed to parse JSON response from ${proxyUrl}:`, e.message);
            continue; // Try next proxy
          }
          
          console.log('UPC Database response:', data);

          if (data.code === 'OK' && data.items && data.items.length > 0) {
            const item = data.items[0];
            
            // Check if this is likely a whisky/spirits product
            const title = (item.title || '').toLowerCase();
            const brand = (item.brand || '').toLowerCase();
            const category = (item.category || '').toLowerCase();
            
            const isSpirits = this.isLikelyWhisky(title) || 
                             this.isLikelyWhisky(brand) ||
                             category.includes('spirits') ||
                             category.includes('alcohol') ||
                             category.includes('beverage');

            if (isSpirits) {
              return this.parseUPCData(item);
            }
          }
          
          // If we get here, this proxy worked but no whisky found
          return null;
          
        } catch (proxyError) {
          console.log(`Proxy ${proxyUrl} failed:`, proxyError.message);
          continue; // Try next proxy
        }
      }
      
      // All proxies failed
      console.log('All UPC Database proxies failed');
      return null;
      
    } catch (error) {
      console.error('UPC Database lookup failed:', error);
      return null;
    }
  }

  async lookupWhiskyBase(barcode) {
    try {
      console.log(`Looking up UPC: ${barcode} on WhiskyBase`);
      
      // Try multiple CORS proxy services for reliability
      const proxies = [
        'https://api.allorigins.win/raw?url=',
        'https://thingproxy.freeboard.io/fetch/'
      ];
      
      for (const proxyUrl of proxies) {
        try {
          const searchUrl = `https://www.whiskybase.com/search?q=${barcode}`;
          const fullUrl = proxyUrl + encodeURIComponent(searchUrl);
          
          console.log(`Trying WhiskyBase proxy: ${proxyUrl}`);
          
          const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
              'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
              'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
            }
          });

          if (!response.ok) {
            console.log(`WhiskyBase proxy ${proxyUrl} returned status: ${response.status}`);
            continue; // Try next proxy
          }

          const html = await response.text();
          console.log('WhiskyBase search response received, parsing...');

          // Parse the search results to find whisky links
          const whiskyLinks = this.parseWhiskyBaseSearchResults(html);
          
          if (whiskyLinks.length === 1) {
            // If exactly one result, fetch the detailed page
            console.log('Found exactly one WhiskyBase result, fetching details...');
            const whiskyData = await this.fetchWhiskyBaseDetails(whiskyLinks[0], proxyUrl);
            if (whiskyData) {
              return whiskyData;
            }
          } else if (whiskyLinks.length === 0) {
            console.log('No WhiskyBase results found for barcode');
            return null;
          } else {
            console.log(`Found ${whiskyLinks.length} WhiskyBase results, skipping (ambiguous)`);
            return null;
          }
          
        } catch (proxyError) {
          console.log(`WhiskyBase proxy ${proxyUrl} failed:`, proxyError.message);
          continue; // Try next proxy
        }
      }
      
      // All proxies failed
      console.log('All WhiskyBase proxies failed');
      return null;
      
    } catch (error) {
      console.error('WhiskyBase lookup failed:', error);
      return null;
    }
  }

  parseWhiskyBaseSearchResults(html) {
    try {
      console.log('Parsing WhiskyBase search results HTML...');
      
      // Try multiple patterns to find whisky links
      const patterns = [
        // Correct WhiskyBase format: /whiskies/whisky/12345
        /<a[^>]+href="(\/whiskies\/whisky\/\d+)"[^>]*>/gi,
        // Alternative with trailing content
        /<a[^>]+href="(\/whiskies\/whisky\/\d+[^"]*)"[^>]*>/gi,
        // Fallback patterns
        /<a[^>]+href="(\/whiskies\/\d+\/[^"]+)"[^>]*>/gi,
        /<a[^>]+href="(\/whisky\/\d+\/[^"]+)"[^>]*>/gi
      ];
      
      let allLinks = [];
      
      for (const pattern of patterns) {
        const matches = html.match(pattern);
        if (matches) {
          const links = matches.map(match => {
            const hrefMatch = match.match(/href="([^"]+)"/);
            return hrefMatch ? hrefMatch[1] : null;
          }).filter(link => link !== null);
          
          allLinks = allLinks.concat(links);
          console.log(`Pattern found ${links.length} links:`, links);
        }
      }
      
      if (allLinks.length === 0) {
        // Debug: log a portion of the HTML to see the structure
        console.log('No whisky links found. HTML sample:', html.substring(0, 1000));
        
        // Try alternative approaches to find whisky IDs
        console.log('Trying alternative ID extraction methods...');
        
        // Look for whisky IDs in JSON data structures
        const idPatterns = [
          // JSON structure: "id":265947 (from the actual HTML analysis)
          /&quot;id&quot;:(\d+)/gi,
          /\"id\":(\d+)/gi,
          /"id":(\d+)/gi,
          // Alternative JSON patterns
          /&amp;quot;id&amp;quot;:(\d+)/gi,
          // Data attributes as fallback
          /data-whisky-id="(\d+)"/gi,
          /data-id="(\d+)"/gi,
          // URL fragments in JavaScript or data
          /whiskies\/whisky\/(\d+)/gi
        ];
        
        let foundIds = [];
        for (const pattern of idPatterns) {
          const matches = html.match(pattern);
          if (matches) {
            const ids = matches.map(match => {
              const idMatch = match.match(/(\d+)/);
              return idMatch ? idMatch[1] : null;
            }).filter(id => id !== null);
            
            if (ids.length > 0) {
              console.log(`Found potential whisky IDs with pattern ${pattern}:`, ids);
              foundIds = foundIds.concat(ids);
            }
          }
        }
        
        // If we found potential IDs, construct URLs and return them
        if (foundIds.length > 0) {
          const uniqueIds = [...new Set(foundIds)];
          const constructedLinks = uniqueIds.map(id => `/whiskies/whisky/${id}`);
          console.log('Constructed whisky links from IDs:', constructedLinks);
          return constructedLinks.slice(0, 5); // Limit to first 5 to avoid too many requests
        }
        
        // Try a very broad search for any links containing "whisky" or "whisk"
        const broadMatches = html.match(/<a[^>]+href="([^"]*whisk[^"]*)"[^>]*>/gi);
        if (broadMatches) {
          console.log('Found broad whisky-related links:', broadMatches.slice(0, 5));
        }
        
        return [];
      }

      // Remove duplicates
      const uniqueLinks = [...new Set(allLinks)];
      
      console.log(`Found ${uniqueLinks.length} unique whisky links in WhiskyBase search:`, uniqueLinks);
      return uniqueLinks;
      
    } catch (error) {
      console.error('Error parsing WhiskyBase search results:', error);
      return [];
    }
  }

  async fetchWhiskyBaseDetails(whiskyPath, proxyUrl) {
    try {
      const whiskyUrl = `https://www.whiskybase.com${whiskyPath}`;
      const fullUrl = proxyUrl + encodeURIComponent(whiskyUrl);
      
      console.log(`Fetching WhiskyBase details from: ${whiskyPath}`);
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
        }
      });

      if (!response.ok) {
        console.log(`WhiskyBase details page returned status: ${response.status}`);
        return null;
      }

      const html = await response.text();
      return this.parseWhiskyBaseDetails(html);
      
    } catch (error) {
      console.error('Error fetching WhiskyBase details:', error);
      return null;
    }
  }

  parseWhiskyBaseDetails(html) {
    try {
      // Extract whisky name from title or main heading
      const nameMatch = html.match(/<h1[^>]*>([^<]+)<\/h1>/i) ||
                       html.match(/<title>([^<]+?)\s*-\s*WhiskyBase/i);
      
      // Extract distillery - often in a specific section
      const distilleryMatch = html.match(/Distillery[^:]*:\s*<[^>]*>([^<]+)</i) ||
                             html.match(/Producer[^:]*:\s*<[^>]*>([^<]+)</i);
      
      // Extract region - look for region information
      const regionMatch = html.match(/Region[^:]*:\s*<[^>]*>([^<]+)</i) ||
                         html.match(/Country[^:]*:\s*<[^>]*>([^<]+)</i);
      
      // Extract age - look for age statement
      const ageMatch = html.match(/Age[^:]*:\s*<[^>]*>([^<]+)</i) ||
                      html.match(/(\d+)\s*(?:year|yr)s?\s*old/i);
      
      // Extract ABV - alcohol percentage
      const abvMatch = html.match(/ABV[^:]*:\s*<[^>]*>([^<]+)</i) ||
                      html.match(/Alcohol[^:]*:\s*<[^>]*>([^<]+)</i) ||
                      html.match(/(\d+(?:\.\d+)?)\s*%/);
      
      // Extract type - whisky type
      const typeMatch = html.match(/Type[^:]*:\s*<[^>]*>([^<]+)</i) ||
                       html.match(/Category[^:]*:\s*<[^>]*>([^<]+)</i);

      let name = nameMatch ? nameMatch[1].trim() : '';
      
      // Clean up WhiskyBase page title suffixes
      name = name.replace(/\s*-\s*Ratings and reviews.*$/i, '');
      name = name.replace(/\s*-\s*WhiskyBase.*$/i, '');
      name = name.trim();
      
      const distillery = distilleryMatch ? distilleryMatch[1].trim() : '';
      const region = regionMatch ? regionMatch[1].trim() : '';
      const age = ageMatch ? (ageMatch[1] || ageMatch[0]).trim() : '';
      const abv = abvMatch ? (abvMatch[1] || abvMatch[0]).replace('%', '').trim() : '';
      const type = typeMatch ? typeMatch[1].trim() : '';

      if (!name) {
        console.log('Could not extract whisky name from WhiskyBase details');
        return null;
      }

      const whiskyData = {
        name: name,
        distillery: distillery || this.extractDistillery(name, ''),
        region: region || this.extractRegion(name),
        age: age || this.extractAge(name),
        abv: abv || this.extractABVFromText(name),
        type: type || this.extractType(name)
      };

      console.log('Extracted whisky data from WhiskyBase:', whiskyData);
      return whiskyData;
      
    } catch (error) {
      console.error('Error parsing WhiskyBase details:', error);
      return null;
    }
  }

  lookupFallbackDatabase(barcode) {
    return this.fallbackDatabase[barcode] || null;
  }

  async lookupGenericProduct(barcode) {
    try {
      // Try Open Food Facts again but be more lenient about whisky detection
      const response = await fetch(`https://world.openfoodfacts.org/api/v0/product/${barcode}.json`);
      const data = await response.json();

      if (data.status === 1 && data.product) {
        const product = data.product;
        const productName = product.product_name || '';
        
        // If the product name contains whisky-related terms, try to parse it
        if (this.isLikelyWhisky(productName)) {
          return this.parseWhiskyFromProduct(product);
        }
      }
      return null;
    } catch (error) {
      console.error('Generic product lookup failed:', error);
      return null;
    }
  }

  isLikelyWhisky(productName) {
    const whiskyTerms = [
      'whisky', 'whiskey', 'scotch', 'bourbon', 'rye',
      'single malt', 'blended', 'highland', 'speyside',
      'islay', 'lowland', 'campbeltown', 'irish',
      'glenfiddich', 'macallan', 'lagavulin', 'ardbeg',
      'glenlivet', 'balvenie', 'laphroaig', 'talisker'
    ];
    
    const lowerName = productName.toLowerCase();
    return whiskyTerms.some(term => lowerName.includes(term));
  }

  parseWhiskyFromProduct(product) {
    const name = product.product_name || 'Unknown Whisky';
    const brand = product.brands || '';
    
    // Try to extract information from the product name and other fields
    const whiskyData = {
      name: name,
      distillery: this.extractDistillery(name, brand),
      region: this.extractRegion(name),
      age: this.extractAge(name),
      abv: this.extractABV(product),
      type: this.extractType(name)
    };

    return whiskyData;
  }

  extractDistillery(name, brand) {
    // Try to extract distillery from brand or name
    if (brand) {
      return brand.split(',')[0].trim();
    }
    
    // Common distillery patterns
    const distilleryPatterns = [
      /^([A-Za-z\s]+?)\s+\d+/,  // "Glenfiddich 12" -> "Glenfiddich"
      /^(The\s+[A-Za-z\s]+?)(?:\s|$)/,  // "The Macallan" -> "The Macallan"
      /^([A-Za-z\s]+?)(?:\s+Single|\s+Blended|\s+Highland|\s+Speyside|\s+Islay)/i
    ];

    for (const pattern of distilleryPatterns) {
      const match = name.match(pattern);
      if (match) {
        return match[1].trim();
      }
    }

    // Fallback: take first word(s) before numbers or common whisky terms
    const words = name.split(' ');
    for (let i = 0; i < words.length; i++) {
      if (/\d/.test(words[i]) || /year|old|single|blended/i.test(words[i])) {
        return words.slice(0, i).join(' ');
      }
    }

    return words[0] || '';
  }

  extractRegion(name) {
    const regions = {
      'speyside': ['speyside'],
      'highlands': ['highland', 'highlands'],
      'islay': ['islay'],
      'lowlands': ['lowland', 'lowlands'],
      'campbeltown': ['campbeltown'],
      'islands': ['islands', 'island'],
      'ireland': ['irish', 'ireland'],
      'america': ['bourbon', 'american', 'kentucky', 'tennessee'],
      'japan': ['japanese', 'japan']
    };

    const lowerName = name.toLowerCase();
    for (const [region, keywords] of Object.entries(regions)) {
      if (keywords.some(keyword => lowerName.includes(keyword))) {
        return region.charAt(0).toUpperCase() + region.slice(1);
      }
    }

    return '';
  }

  extractAge(name) {
    // Look for age patterns like "12 Year Old", "18yo", "21 years"
    const agePatterns = [
      /(\d+)\s*(?:year|yr)s?\s*old/i,
      /(\d+)\s*yo/i,
      /(\d+)\s*years?/i,
      /aged?\s*(\d+)/i
    ];

    for (const pattern of agePatterns) {
      const match = name.match(pattern);
      if (match) {
        return `${match[1]} years`;
      }
    }

    // Check for NAS (No Age Statement) indicators
    if (/no age|NAS|cask strength|reserve|select/i.test(name)) {
      return 'NAS';
    }

    return '';
  }

  extractABV(product) {
    // Try to get ABV from various fields
    const abvSources = [
      product.alcohol_100g,
      product.alcohol,
      product.nutriments?.alcohol,
      product.nutriments?.alcohol_100g
    ];

    for (const abv of abvSources) {
      if (abv && !isNaN(abv)) {
        return String(Math.round(parseFloat(abv) * 10) / 10);
      }
    }

    // Try to extract from product name
    const abvMatch = (product.product_name || '').match(/(\d+(?:\.\d+)?)\s*%/);
    if (abvMatch) {
      return abvMatch[1];
    }

    return '';
  }

  extractType(name) {
    const lowerName = name.toLowerCase();
    
    if (lowerName.includes('single malt')) return 'Single Malt';
    if (lowerName.includes('blended')) return 'Blended';
    if (lowerName.includes('bourbon')) return 'Bourbon';
    if (lowerName.includes('rye')) return 'Rye';
    if (lowerName.includes('irish')) return 'Irish';
    if (lowerName.includes('japanese')) return 'Japanese';
    if (lowerName.includes('grain')) return 'Grain';
    
    return 'Single Malt'; // Default assumption
  }

  parseUPCData(item) {
    // Parse UPC Database API response
    const name = item.title || '';
    const brand = item.brand || '';
    const description = item.description || '';
    
    console.log('Parsing UPC data:', { name, brand, description });
    
    // Combine name and description for better parsing
    const fullText = `${name} ${description}`.trim();
    
    const whiskyData = {
      name: name,
      distillery: this.extractDistillery(fullText, brand),
      region: this.extractRegion(fullText),
      age: this.extractAge(fullText),
      abv: this.extractABVFromText(fullText),
      type: this.extractType(fullText)
    };

    console.log('Extracted whisky data:', whiskyData);
    return whiskyData;
  }

  extractABVFromText(text) {
    // Extract ABV from text (for UPC data that doesn't have structured fields)
    const abvMatch = text.match(/(\d+(?:\.\d+)?)\s*%\s*(?:abv|alc|alcohol)?/i);
    if (abvMatch) {
      return abvMatch[1];
    }
    
    // Look for "proof" (US measurement: proof = 2 × ABV)
    const proofMatch = text.match(/(\d+(?:\.\d+)?)\s*proof/i);
    if (proofMatch) {
      const abv = parseFloat(proofMatch[1]) / 2;
      return String(Math.round(abv * 10) / 10);
    }
    
    return '';
  }

  combineWhiskyData(openFoodFactsData, upcDatabaseData) {
    console.log('Combining data from both sources...');
    
    // Helper function to determine which value is "better" (more complete/accurate)
    const getBestValue = (off, upc, fieldName) => {
      // Prefer non-empty values
      if (!off && upc) return upc;
      if (off && !upc) return off;
      if (!off && !upc) return '';
      
      // Both have values - apply field-specific logic
      switch (fieldName) {
        case 'name':
          // Prefer longer, more descriptive names
          return off.length > upc.length ? off : upc;
          
        case 'distillery':
          // Prefer the one that looks more like a proper distillery name
          if (off.toLowerCase().includes('distillery') || off.toLowerCase().includes('ltd')) return off;
          if (upc.toLowerCase().includes('distillery') || upc.toLowerCase().includes('ltd')) return upc;
          return off.length > upc.length ? off : upc;
          
        case 'region':
          // Prefer known whisky regions
          const knownRegions = ['highlands', 'speyside', 'islay', 'lowlands', 'campbeltown', 'islands', 'ireland', 'scotland', 'america', 'japan'];
          const offRegion = off.toLowerCase();
          const upcRegion = upc.toLowerCase();
          
          if (knownRegions.includes(offRegion) && !knownRegions.includes(upcRegion)) return off;
          if (knownRegions.includes(upcRegion) && !knownRegions.includes(offRegion)) return upc;
          return off; // Default to Open Food Facts
          
        case 'age':
          // Prefer specific age statements over generic ones
          if (off.includes('years') && !upc.includes('years')) return off;
          if (upc.includes('years') && !off.includes('years')) return upc;
          if (off === 'NAS' && upc !== 'NAS') return upc;
          if (upc === 'NAS' && off !== 'NAS') return off;
          return off;
          
        case 'abv':
          // Prefer the one that looks like a valid ABV (reasonable range)
          const offAbv = parseFloat(off);
          const upcAbv = parseFloat(upc);
          
          if (isNaN(offAbv) && !isNaN(upcAbv)) return upc;
          if (!isNaN(offAbv) && isNaN(upcAbv)) return off;
          
          // Both are numbers - prefer the one in reasonable whisky range (35-70%)
          if (offAbv >= 35 && offAbv <= 70 && (upcAbv < 35 || upcAbv > 70)) return off;
          if (upcAbv >= 35 && upcAbv <= 70 && (offAbv < 35 || offAbv > 70)) return upc;
          
          return off; // Default to Open Food Facts
          
        case 'type':
          // Prefer more specific whisky types
          const specificTypes = ['single malt', 'blended', 'bourbon', 'rye', 'irish', 'japanese'];
          const offType = off.toLowerCase();
          const upcType = upc.toLowerCase();
          
          if (specificTypes.includes(offType) && !specificTypes.includes(upcType)) return off;
          if (specificTypes.includes(upcType) && !specificTypes.includes(offType)) return upc;
          return off;
          
        default:
          return off; // Default to Open Food Facts
      }
    };

    const combined = {
      name: getBestValue(openFoodFactsData.name, upcDatabaseData.name, 'name'),
      distillery: getBestValue(openFoodFactsData.distillery, upcDatabaseData.distillery, 'distillery'),
      region: getBestValue(openFoodFactsData.region, upcDatabaseData.region, 'region'),
      age: getBestValue(openFoodFactsData.age, upcDatabaseData.age, 'age'),
      abv: getBestValue(openFoodFactsData.abv, upcDatabaseData.abv, 'abv'),
      type: getBestValue(openFoodFactsData.type, upcDatabaseData.type, 'type')
    };

    console.log('Combined result:', combined);
    console.log('Sources used:', {
      name: combined.name === openFoodFactsData.name ? 'OpenFoodFacts' : 'UPCDatabase',
      distillery: combined.distillery === openFoodFactsData.distillery ? 'OpenFoodFacts' : 'UPCDatabase',
      region: combined.region === openFoodFactsData.region ? 'OpenFoodFacts' : 'UPCDatabase',
      age: combined.age === openFoodFactsData.age ? 'OpenFoodFacts' : 'UPCDatabase',
      abv: combined.abv === openFoodFactsData.abv ? 'OpenFoodFacts' : 'UPCDatabase',
      type: combined.type === openFoodFactsData.type ? 'OpenFoodFacts' : 'UPCDatabase'
    });

    return combined;
  }

  combineMultipleWhiskyData(dataSources) {
    console.log(`Combining data from ${dataSources.length} sources...`);
    
    // Helper function to get the best value from multiple sources
    const getBestValueFromMultiple = (sources, fieldName) => {
      const values = sources.map(source => source[fieldName]).filter(val => val && val.trim());
      
      if (values.length === 0) return '';
      if (values.length === 1) return values[0];
      
      // Apply field-specific logic for multiple values
      switch (fieldName) {
        case 'name':
          // Prefer the longest, most descriptive name
          return values.reduce((best, current) => 
            current.length > best.length ? current : best
          );
          
        case 'distillery':
          // Prefer names with distillery indicators
          const withDistillery = values.filter(val => 
            val.toLowerCase().includes('distillery') || val.toLowerCase().includes('ltd')
          );
          if (withDistillery.length > 0) {
            return withDistillery.reduce((best, current) => 
              current.length > best.length ? current : best
            );
          }
          return values.reduce((best, current) => 
            current.length > best.length ? current : best
          );
          
        case 'region':
          // Prefer known whisky regions
          const knownRegions = ['highlands', 'speyside', 'islay', 'lowlands', 'campbeltown', 'islands', 'ireland', 'scotland', 'america', 'japan'];
          const validRegions = values.filter(val => 
            knownRegions.includes(val.toLowerCase())
          );
          if (validRegions.length > 0) return validRegions[0];
          return values[0]; // Default to first available
          
        case 'age':
          // Prefer specific age statements
          const withYears = values.filter(val => val.includes('years'));
          if (withYears.length > 0) return withYears[0];
          
          const nonNAS = values.filter(val => val !== 'NAS');
          if (nonNAS.length > 0) return nonNAS[0];
          
          return values[0];
          
        case 'abv':
          // Prefer valid ABV values in whisky range
          const validABVs = values.filter(val => {
            const abv = parseFloat(val);
            return !isNaN(abv) && abv >= 35 && abv <= 70;
          });
          if (validABVs.length > 0) return validABVs[0];
          
          const numericABVs = values.filter(val => !isNaN(parseFloat(val)));
          if (numericABVs.length > 0) return numericABVs[0];
          
          return values[0];
          
        case 'type':
          // Prefer specific whisky types
          const specificTypes = ['single malt', 'blended', 'bourbon', 'rye', 'irish', 'japanese'];
          const specificMatches = values.filter(val => 
            specificTypes.includes(val.toLowerCase())
          );
          if (specificMatches.length > 0) return specificMatches[0];
          return values[0];
          
        default:
          return values[0]; // Default to first available
      }
    };

    const combined = {
      name: getBestValueFromMultiple(dataSources, 'name'),
      distillery: getBestValueFromMultiple(dataSources, 'distillery'),
      region: getBestValueFromMultiple(dataSources, 'region'),
      age: getBestValueFromMultiple(dataSources, 'age'),
      abv: getBestValueFromMultiple(dataSources, 'abv'),
      type: getBestValueFromMultiple(dataSources, 'type')
    };

    console.log('Multi-source combined result:', combined);
    
    // Log which sources contributed to each field
    const sourceContributions = {};
    Object.keys(combined).forEach(field => {
      const value = combined[field];
      const contributingSource = dataSources.findIndex(source => source[field] === value);
      sourceContributions[field] = contributingSource >= 0 ? 
        ['OpenFoodFacts', 'UPCDatabase', 'WhiskyBase'][contributingSource] : 'None';
    });
    
    console.log('Source contributions:', sourceContributions);
    return combined;
  }
}

export const whiskyLookupService = new WhiskyLookupService();
